import Modal from 'react-bootstrap/Modal';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useEffect, useState } from 'react';
import urls from '../../constants';
import { hasListPriceRoute } from '../../hooks/useListPriceCalculation';


export const ProductImportFile = ({
    modalShow,
    setModalShow,
    productVariant,
    setAddProduct,
    addProduct,
    productConfigVariant,
    variantsArray,
    setvariantsarray,
    HandleProductOnChange,
    setRecalculateLoading,
    productConfig,
    endcaplist,
    optionsMap = {},
    combinations = [],
    onManualSizeImport }) => {
    const [excelData, setExcelData] = useState([]);
    const [file, setFile] = useState(null);
    const [excelUpdate, setExcelUpdate] = useState(false);
    const [variantsData, setVariantsData] = useState([]);
    const [productsData, setProductsData] = useState([]);
    const [errorShow, setErrorShow] = useState(null);
    useEffect(() => {
        setFile(null);
        setExcelData([]);
        setExcelUpdate(false);
        setErrorShow(null);
    }, [modalShow])

    // The Excel Unit Price column is required when this product has no
    // List Price API route or no configured List Price field.
    const hasConfiguredListPrice = productVariant?.some((variant) =>
        urls?.LIST_PRICE_CONSTANT?.includes(variant?.variantId)
    );
    const isManualTemplate =
        (!hasListPriceRoute(productConfig?.productName) || !hasConfiguredListPrice) &&
        productConfigVariant?.some((variant) =>
            urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId)
        );

    const getVariantColumnName = (variant) => {
        if (urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId)) return variant?.variantName || "Unit Price";
        return variant?.variantName || "";
    };

    const isDerivedActuatorVariant = (variant) => [
        ...(urls?.ACTUATOR_MODEL_CONSTANT || []),
        ...(urls?.ACTUATOR_QTY_CONSTANT || []),
        ...(urls?.ACTUATOR_COST_CONSTANT || []),
    ].includes(Number(variant?.variantId));

    // The VAV Model is derived from the selected inlet/combination in the
    // quotation grid. It must not be an Excel input or a required header.
    const isAutoFilledVavModelVariant = (variant) =>
        urls?.VAV_MODEL?.includes(Number(variant?.variantId));

    const getTemplateVariantsThroughUnitPrice = () => {
        const configuredVariants = productConfigVariant || [];
        const unitPriceIndex = configuredVariants.findIndex((variant) =>
            urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId)
        );
        const variantsThroughUnitPrice = unitPriceIndex >= 0
            ? configuredVariants.slice(0, unitPriceIndex + 1)
            : configuredVariants;

        return variantsThroughUnitPrice.filter((variant) =>
            getVariantColumnName(variant) &&
            !urls?.LIST_PRICE_CONSTANT?.includes(variant?.variantId) &&
            !urls?.NET_LIST_PRICE_CONSTANT?.includes(variant?.variantId) &&
            !urls?.NET_UNIT_PRICE_CONSTANT?.includes(variant?.variantId) &&
            !isDerivedActuatorVariant(variant) &&
            !isAutoFilledVavModelVariant(variant) &&
            getVariantColumnName(variant).trim() !== "#"
        );
    };

    const normalizeHeader = (value) => String(value ?? "").trim().toLowerCase();
    const isTagReferenceVariant = (variant) =>
        normalizeHeader(variant?.variantName).replace(/\s+/g, "") === "tagref";

    const getManualVariantOptions = (variant) =>
        Array.isArray(optionsMap?.[variant?.variantId]?.options)
            ? optionsMap[variant.variantId].options.filter((option) => option !== null && option !== undefined && option !== "")
            : [];

    const optionValue = (option) => String(option?.value ?? option?.label ?? option ?? "").trim();
    const valuesMatch = (left, right) => {
        const leftValue = optionValue(left);
        const rightValue = optionValue(right);
        if (leftValue === rightValue) return true;

        const leftNumber = Number(leftValue.replace(/,/g, ""));
        const rightNumber = Number(rightValue.replace(/,/g, ""));
        return leftValue !== "" && rightValue !== "" &&
            Number.isFinite(leftNumber) && Number.isFinite(rightNumber) &&
            leftNumber === rightNumber;
    };

    const exportManualSizeTemplate = () => {
        const manualVariants = getTemplateVariantsThroughUnitPrice();
        const configuredColumns = manualVariants.map(getVariantColumnName);
        const worksheet = XLSX.utils.aoa_to_sheet([configuredColumns]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sizes");

        const optionRows = [["Variant", "Allowed options"]];
        manualVariants.forEach((variant) => {
            const options = getManualVariantOptions(variant);
            if (options.length) optionRows.push([getVariantColumnName(variant), options.join(", ")]);
        });
        if (Array.isArray(combinations) && combinations.length) {
            optionRows.push([]);
            optionRows.push(["Valid variant combinations", ""]);
            combinations.forEach((combination) => {
                const description = (combination?.options || [])
                    .map((option) => {
                        const variant = manualVariants.find((item) => String(item?.variantId) === String(option?.variantId));
                        return variant ? `${getVariantColumnName(variant)}: ${option?.value}` : "";
                    })
                    .filter(Boolean)
                    .join(" | ");
                if (description) optionRows.push([description, ""]);
            });
        }
        if (optionRows.length > 1) {
            const optionsSheet = XLSX.utils.aoa_to_sheet(optionRows);
            optionsSheet["!cols"] = [{ wch: 30 }, { wch: 80 }];
            XLSX.utils.book_append_sheet(workbook, optionsSheet, "Product options");
        }
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), `${productConfig?.productName?.productName || "product"}-manual-sizes.xlsx`);
    };

    const exportToExcel = () => {
        // API-priced products receive their Unit Price from the List Price
        // endpoint, so do not ask users to enter it in their import sheet.
        const templateVariants = getTemplateVariantsThroughUnitPrice().filter((variant) =>
            !urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId)
        );
        const data = templateVariants.reduce((acc, item) => {
            acc[getVariantColumnName(item)] = "";
            return acc;
        }, {});
        // Convert JSON data to a worksheet
        const worksheet = XLSX.utils.json_to_sheet([data]);

        // Create a new workbook and append the worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        // Generate the Excel file and trigger download
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(fileData, productConfig?.productName?.productName + "-sampleExcel.xlsx");
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        const isExcelFile = /\.(xls|xlsx)$/i.test(selectedFile?.name || "");

        if (selectedFile && !isExcelFile) {
            setFile(null);
            setErrorShow("Only .xls or .xlsx files are supported.");
            event.target.value = '';
            return;
        }

        setFile(selectedFile || null)
        setErrorShow(null)
        event.target.value = '';
    }

    const handleFileUpload = () => {
        if (file) {
            setErrorShow(null);
            const reader = new FileReader();

            reader.onload = (e) => {
                const arrayBuffer = e.target.result;
                const uint8Array = new Uint8Array(arrayBuffer); // Convert ArrayBuffer to Uint8Array
                // Read workbook from the Uint8Array
                const workbook = XLSX.read(uint8Array, { type: "array" });
                const sheetName = workbook.SheetNames[0]; // Get the first sheet name
                if (!sheetName) {
                    setErrorShow("The uploaded workbook does not contain any sheets.");
                    setFile(null);
                    return;
                }
                const sheet = workbook.Sheets[sheetName]; // Get sheet data

                const data = XLSX.utils.sheet_to_json(sheet); // Convert sheet data to JSON
                if (!data?.length) {
                    setErrorShow("The uploaded sheet does not contain any rows.");
                    setFile(null);
                    return;
                }
                const keys = Object.keys(data[0]);
                const normalizedHeaders = Object.fromEntries(keys.map((key) => [normalizeHeader(key), key]));
                const unitPriceVariant = getTemplateVariantsThroughUnitPrice().find((variant) =>
                    urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId)
                );
                const unitPriceHeader = unitPriceVariant && normalizedHeaders[normalizeHeader(getVariantColumnName(unitPriceVariant))];

                // Only products that have no API list-price variant use the
                // price entered in Excel. Configured products must still go
                // through their list-price endpoint after import, even when a
                // workbook happens to contain a Unit Price column.
                if (isManualTemplate && unitPriceHeader) {
                    const manualVariants = getTemplateVariantsThroughUnitPrice();
                    const missingColumns = manualVariants.filter(
                        (variant) => !isTagReferenceVariant(variant) &&
                            !normalizedHeaders[normalizeHeader(getVariantColumnName(variant))]
                    );
                    if (missingColumns.length) {
                        setErrorShow(`The workbook is missing: ${missingColumns.map(getVariantColumnName).join(", ")}.`);
                        setFile(null);
                        return;
                    }

                    const manualRows = data.map((row) => {
                        const values = {};
                        manualVariants.forEach((variant) => {
                            const columnName = getVariantColumnName(variant);
                            const header = normalizedHeaders[normalizeHeader(columnName)];
                            values[columnName] = row?.[header] ?? "";
                        });
                        return {
                            values,
                            price: Number(String(values[getVariantColumnName(unitPriceVariant)] ?? "").replace(/,/g, "")),
                        };
                    });

                    const invalidOption = manualRows.some(({ values }) => manualVariants.some((variant) => {
                        const allowed = getManualVariantOptions(variant);
                        const value = String(values[getVariantColumnName(variant)] ?? "").trim();
                        const numericValue = Number(value.replace(/,/g, ""));
                        const isNumericValue = value !== "" && Number.isFinite(numericValue);
                        return !urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId) &&
                            !isNumericValue &&
                            allowed.length > 0 && value !== "" &&
                            !allowed.some((option) => valuesMatch(option, value));
                    }));
                    if (invalidOption) {
                        setErrorShow("The workbook contains a variant value that is not configured for this product.");
                        setFile(null);
                        return;
                    }

                    const combinationVariantIds = new Set((combinations || []).flatMap((combo) =>
                        (combo?.options || []).map((option) => String(option?.variantId))
                    ));
                    const hasInvalidCombination = combinationVariantIds.size > 1 && manualRows.some(({ values }) => {
                        const entered = manualVariants
                            .filter((variant) => combinationVariantIds.has(String(variant?.variantId)) && String(values[getVariantColumnName(variant)] ?? "").trim() !== "")
                            .map((variant) => ({ variantId: variant.variantId, value: String(values[getVariantColumnName(variant)]).trim() }));
                        return entered.length > 1 && !combinations.some((combo) =>
                            entered.every((entry) => (combo?.options || []).some((option) =>
                                String(option?.variantId) === String(entry.variantId) && valuesMatch(option?.value, entry.value)
                            ))
                        );
                    });
                    if (hasInvalidCombination) {
                        setErrorShow("The workbook contains a variant combination that is not configured for this product.");
                        setFile(null);
                        return;
                    }

                    if (!manualRows.length || manualRows.some((row) => !Number.isFinite(row.price) || row.price < 0)) {
                        setErrorShow("Each row needs a valid Unit Price.");
                        setFile(null);
                        return;
                    }

                    AddProduct(manualRows.map((row) => row.values));
                    setModalShow(false);
                    return;
                }
                const headName = productConfigVariant?.filter((item) =>
                    item?.inputByUser === true && !isDerivedActuatorVariant(item)
                )
                const requiredHeadName = headName?.filter(item =>
                    urls?.VALIDATION_VARIANT_ARRAY?.includes(item?.variantId) &&
                    !isTagReferenceVariant(item) &&
                    !isAutoFilledVavModelVariant(item)
                );
                const isSame = requiredHeadName.every(item1 => keys.some(item2 => item1.variantName === item2));
                const hasValidData = data?.some(row =>
                    requiredHeadName?.some(
                        headVar => row?.[headVar?.variantName] !== ""
                    )
                );

                if (isSame && data?.length > 0 && hasValidData) {
                    setRecalculateLoading(true);
                    setModalShow(false);
                    setExcelData(data); // Save the extracted data
                    AddProduct(data)
                }
                else {
                    setErrorShow("Please check the template columns and required size values.")
                    setFile(null);
                }
            };
            reader.readAsArrayBuffer(file); // Read file as ArrayBuffer
        } else {
            setErrorShow("Please upload the file")
        }
    };

    const AddProduct = (data) => {
        if (!data?.length) return;
        const newProducts = [];
        const newVariants = [];
        data.forEach((excelData) => {
            const generated = new Set();
            let uniqueNumber;

            do {
                uniqueNumber = Date.now() + Math.floor(Math.random() * 1000000);
            } while (generated.has(uniqueNumber));

            generated.add(uniqueNumber);

            newProducts.push({
                sid: uniqueNumber,
                id: 0,
                quoteProductId: 0,
                notes: "",
                specialDesignRequest: "",
                sdrCharges: "",
            });

            const variantDetails = productConfigVariant || productVariant;
            if (variantDetails?.length) {
                const unitPriceVariant = variantDetails.find((variant) =>
                    urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId)
                );
                const quantityVariant = variantDetails.find((variant) =>
                    urls?.QUANTITY_CONSTANT?.includes(variant?.variantId)
                );
                const toNumber = (value) => Number(String(value ?? "").replace(/,/g, ""));
                const unitPriceValue = toNumber(excelData?.[getVariantColumnName(unitPriceVariant)]);
                const quantityValue = toNumber(excelData?.[getVariantColumnName(quantityVariant)]);
                variantDetails.forEach((item) => {
                    const isNetTotal = urls?.NET_UNIT_PRICE_CONSTANT?.includes(item?.variantId);
                    const isNetUnitPrice = urls?.NET_LIST_PRICE_CONSTANT?.includes(item?.variantId);
                    const isUnitPrice = urls?.UNIT_PRICE_CONSTANT?.includes(item?.variantId);
                    newVariants.push({
                        sid: uniqueNumber,
                        id: 0,
                        quoteProductItemId: 0,
                        variantId: item?.variantId,
                        // Calculate the displayed Total Price as soon as the
                        // Excel row is added.  This must not depend on the
                        // list-price API because imported Unit Price rows may
                        // be priced manually.
                        variantValue: isNetTotal
                            ? String((Number.isFinite(unitPriceValue) ? unitPriceValue : 0) * (Number.isFinite(quantityValue) ? quantityValue : 0))
                            // The pricing API expects the converted Unit Price
                            // in both the Net Unit Price (13) and Unit Price
                            // (14) variants.  The displayed value is converted
                            // from this base value by the size grid.
                            : isNetUnitPrice
                                ? String(Number.isFinite(unitPriceValue) ? unitPriceValue : 0)
                            : urls.END_CAPS.includes(item?.variantId) ?
                            endcaplist?.find(item1 => item1?.value === excelData?.[getVariantColumnName(item)]) ?
                                excelData?.[getVariantColumnName(item)] || ""
                                : ""
                            : excelData?.[getVariantColumnName(item)] ?? "",
                        // Unit price is intentionally user-editable for this
                        // isolated Excel/manual-pricing flow.
                        inputByUser: isManualTemplate && isUnitPrice ? true : item?.inputByUser,
                    });
                });
            }
        });
        const emptyList = [...variantsArray, ...newVariants]
            ?.filter(item =>
                item?.inputByUser === true &&
                item?.variantValue === "" &&
                urls?.VALIDATION_VARIANT_ARRAY?.includes(item?.variantId)
            )
            ?.map(item => item?.sid);

        setAddProduct((prev) => [
            ...prev.filter(item => !emptyList?.includes(item?.sid)),
            ...newProducts
        ]);
        setvariantsarray((prev) => [
            ...prev.filter(item => !emptyList?.includes(item?.sid)),
            ...newVariants
        ]);
        setExcelUpdate(true);
        setVariantsData(newVariants)
        setProductsData(newProducts)
    };

    useEffect(() => {
        if (excelUpdate && variantsData?.length > 0 && productsData?.length > 0) {


            const processSequentially = async () => {
                if (isManualTemplate) return;
                for (let productIndex = 0; productIndex < addProduct.length; productIndex++) {
                    const productItem = addProduct[productIndex];
                    try {
                        await HandleProductOnChange({ sid: productItem?.sid }, productIndex);
                    } catch (error) {
                        console.error(
                            `Error processing product at sid ${productItem?.sid}:`,
                            error
                        );
                    }
                }
            };

            processSequentially()
                .then(() => {
                    setRecalculateLoading(false);
                    setExcelUpdate(false);
                    setVariantsData([])
                    setProductsData([])
                })
                .catch((error) => {
                    console.error("Error processing products:", error);
                });
        }
    }, [excelUpdate, variantsData, productsData]);


    return (<>
        <Modal
            size="sm"
            aria-labelledby="contained-modal-title-vcenter"
            centered
            show={modalShow}
            className="upload-modal-popup"
        >
            <Modal.Header>
                <div className="product-import-header">
                    <div>
                        <h5>Import Sizes</h5>
                        <span>{productConfig?.productName?.productName || "Selected model"}</span>
                    </div>
                    <button type="button" className="product-import-close" aria-label="Close" onClick={() => setModalShow(false)}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>
            </Modal.Header>
            <Modal.Body>
                {errorShow && (
                    <div className="product-import-error">{errorShow}</div>
                )}

                <div className="product-import-steps">
                    <button
                        className="product-import-template"
                        type="button"
                        onClick={() => { isManualTemplate ? exportManualSizeTemplate() : exportToExcel() }}
                    >
                        <i className="fas fa-file-download"></i>
                        <span>
                            Download template
                            <small>{isManualTemplate ? "Uses this product's configured variant columns and Unit Price. Total Price is calculated in the quotation." : "Use the generated columns for this model."}</small>
                        </span>
                    </button>

                    <div className="product-import-dropzone">
                        <form id="file-upload-form" className="uploader">
                            <input
                                id="file-upload"
                                type="file"
                                name="productSizeImportFile"
                                accept=".xls,.xlsx"
                                onChange={handleFileChange}
                            />
                            <label htmlFor="file-upload" className={file ? "has-file" : ""}>
                                <span className="material-symbols-outlined upload-icon">
                                    cloud_upload
                                </span>
                                <span className="attach-label">
                                    <strong>{file?.name || "Choose Excel file"}</strong>
                                    <small>Only .xls or .xlsx files are supported.</small>
                                </span>
                            </label>
                        </form>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className="btn cancel-btn" type="button" onClick={() => setModalShow(false)}>
                    Cancel
                </button>
                <button className="btn upload-btn" type="button" onClick={() => handleFileUpload()}>
                    Upload
                </button>
            </Modal.Footer>
        </Modal>
    </>
    )
}




